<template>
  <div class="login-wrapper">
    <!-- Login Card -->
    <div class="login-card">
      <!-- Login Header -->
      <div class="login-header">
        <div class="user-icon-wrapper">
          <svg
            class="user-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            ></path>
          </svg>
        </div>
        <h2 class="login-title">Đăng Nhập</h2>
        <p class="login-subtitle">Tra cứu lịch sử mua hàng của bạn</p>
      </div>

      <div class="form-container">
        <span v-if="errors.message" class="error-message">{{
          errors.message[0]
        }}</span>
        <div class="form-group">
          <label class="form-label required">Số điện thoại</label>
          <div class="input-wrapper">
            <input
              type="tel"
              id="phoneNumber"
              placeholder="Nhập số điện thoại"
              class="form-input"
              v-model="form.phone"
              @blur="validatePhone"
            />
          </div>
          <span v-if="errors.phone" class="error-message">
            {{ errors.phone[0] }}
          </span>
        </div>
        <div class="form-group">
          <label class="form-label required" for="">Email</label>
          <div class="input-wrapper">
            <input
              type="email"
              class="form-input"
              placeholder="Nhập email nhận OTP"
              v-model="form.email"
              @blur="validateEmail"
            />
          </div>
          <span v-if="errors.email" class="error-message">
            {{ errors.email[0] }}
          </span>
        </div>

        <div class="form-group" id="otpField">
          <label class="form-label">Mã OTP</label>
          <div class="otp-container">
            <input
              type="text"
              placeholder="Nhập mã OTP"
              class="otp-input"
              v-model="otp"
              maxlength="6"
            />
            <button
              type="button"
              @click="sendOTP()"
              class="otp-button"
              :disabled="!reSendOtp || loading"
              :class="{ disabled: !reSendOtp }"
            >
              <span v-if="loading" class="spinner"></span>
              <span v-else>{{ otpButtonText }}</span>
            </button>
          </div>
          <span v-if="errors.otp" class="error-message">{{
            errors.otp[0]
          }}</span>
          <p v-if="otpSent && !reSendOtp" class="info-message">
            {{ formattedCountdown }}
          </p>
        </div>
        <!-- Remember Me Section -->
        <div class="remember-section" id="rememberSection">
          <label class="remember-checkbox">
            <input type="checkbox" class="checkbox" />
            <span class="checkbox-label">Ghi nhớ đăng nhập</span>
          </label>
          <a href="#" class="forgot-link">Quên mật khẩu?</a>
        </div>

        <!-- Submit Button -->
        <button
          @click="handleLogin()"
          class="submit-btn"
          :disabled="loading"
          :class="{ loading: loading }"
        >
          <span v-if="loading" class="spinner"></span>
          <span v-else>Đăng Nhập</span>
        </button>
      </div>

      <!-- Divider -->
      <div class="divider">
        <div class="divider-line">
          <div class="divider-border"></div>
        </div>
        <div class="divider-text-wrapper">
          <span class="divider-text">Hoặc</span>
        </div>
      </div>

      <!-- Register Section -->
      <div class="register-section">
        <p class="register-text">
          Chưa có tài khoản?
          <router-link to="/register-customer.html" class="register-link"
            >Đăng ký ngay</router-link
          >
        </p>
      </div>
    </div>

    <!-- Benefits Grid -->
    <div class="benefits-grid">
      <div class="benefit-card">
        <span class="benefit-icon">🚚</span>
        <p class="benefit-text">Giao hàng nhanh</p>
      </div>
      <div class="benefit-card">
        <span class="benefit-icon">💯</span>
        <p class="benefit-text">Hàng chính hãng</p>
      </div>
      <div class="benefit-card">
        <span class="benefit-icon">🎁</span>
        <p class="benefit-text">Ưu đãi hấp dẫn</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useToast } from "@/composables/useToast";
import { customerApi } from "@/services/api";
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const form = reactive({
  phone: "",
  email: "",
});
const errors = ref({});
const loading = ref(false);
const countdown = ref(0);
const otpSent = ref(false);
const otp = ref("");

const reSendOtp = computed(() => countdown.value === 0);
const formattedCountdown = computed(() => `Gửi lại sau ${countdown.value}s`);
const otpButtonText = computed(() => {
  if (!otpSent.value) {
    return "Gửi mã";
  }
  return reSendOtp.value ? "Gửi lại" : formattedCountdown.value;
});
const validatePhone = () => {
  const phoneRegex = /(84|0[3|5|7|8|9])+([0-9]{8})\b/;
  if (!form.phone) {
    errors.value.phone = ["Vui lòng nhập số điện thoại"];
    return false;
  }
  if (!phoneRegex.test(form.phone)) {
    errors.value.phone = ["Số điện thoại không hợp lệ"];
    return false;
  }
  errors.value.phone = null;
  return true;
};

const validateEmail = () => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!form.email) {
    errors.value.email = ["Vui lòng nhập email để nhận mã OTP"];
    return false;
  }
  if (!regex.test(form.email)) {
    errors.value.email = ["Email không đúng định dạng (ví dụ: abc@gmail.com)"];
    return false;
  }
  errors.value.phone = null;
  return true;
};
const sendOTP = async () => {
  if (!validatePhone() || !validateEmail() ) return;

  loading.value = true;
  errors.value = {};
  const dataSend = {
    phone: form.phone,
    email: form.email
  };
  try {
    const response = await customerApi.sendOtp(dataSend);
    if (response.data.success) {
      otpSent.value = true;
      startCountdown(60);
      toast.success("Mã OTP đã được gửi. Vui lòng kiểm tra Tin nhắn hoặc Email!");
    }
  } catch (error) {
    console.error("Lỗi khi gửi OTP:", error);
    errors.value.otp = ["Không thể gửi mã OTP. Vui lòng thử lại."];
  } finally {
    loading.value = false;
  }
};
const startCountdown = (seconds) => {
  countdown.value = seconds;
  const interval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(interval);
    }
  }, 1000);
};
const handleLogin = async () => {
  await loginWithOtp();
};
const loginWithOtp = async () => {
  if (!otp.value || !validatePhone()) {
    errors.value.message = ["Vui lòng nhập đầy đủ thông tin"];
    return;
  }
  loading.value = true;
  errors.value = {};
  try {
    const response = await customerApi.loginWithOtp({
      phone: form.phone,
      otp: otp.value,
    });
    const token = response.data.access_token;
    localStorage.setItem("account_customer_token", JSON.stringify(token));
    router.push("/lich-su-mua-hang.html");
    toast.success("Đăng nhập thành công!");
  } catch (error) {
    console.error("Lỗi khi đăng nhập với OTP:", error);
    errors.value.message = [
      "Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin.",
    ];
    const errorMessage =
      error.response?.data?.message || "Có lỗi xảy ra khi đăng nhập";
    toast.error(errorMessage);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (route.query.message === "expired") {
    toast.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
    const newQuery = { ...route.query };
    delete newQuery.message;
    router.replace({ query: newQuery });
  }

  const token = localStorage.getItem("account_customer_token");
  if (token && !route.query.message) {
    router.push("/lich-su-mua-hang.html");
  }
});
</script>
<style scoped>
/* Error Message */
.error-message {
  display: block;
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

/* Info Message */
.info-message {
  display: block;
  color: #3b82f6;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

/* Disabled Button */
.otp-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading Spinner */
.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Loading Button */
.submit-btn.loading {
  opacity: 0.8;
  cursor: not-allowed;
}
</style>
